/* THE PLACEMENT ORACLE, frontend mirror (W6 REV 3).
 *
 * Byte-for-byte the same ruling as src/nolos/workbench/placement.py.
 * tests/workbench/test_placement_oracle.py parses THIS file and fails
 * the build if the two drift — so a new task type cannot land on one
 * side only, and nothing can silently vanish from the workbench.
 *
 * Four modes, split by WHO CAN ACT — and the split runs through the
 * VERB, not across the task:
 *   queue          — officer work; a selectable row in its surface's
 *                    stream.
 *   status         — Record Keeping walk-view status ("no chain head
 *                    yet" / "no target yet"); placed, never counted.
 *   admin          — a MACHINE failure. Prints on the OTHER OPS line as
 *                    "N system failures · admin", never a queue row and
 *                    never in an officer total — but whole-group RETRY
 *                    stays with the officer on two of the three kinds.
 *                    Admin keeps diagnosis, partial retry and code
 *                    fixes; `unrouted` is admin-only. A retry that
 *                    dead-letters again says so — never a silent
 *                    requeue.
 *   document_state — not work: a state of a DOCUMENT on the Documents
 *                    page. Acting on it MINTS a request, which then
 *                    re-enters Chase & Request like any other request.
 */

export type Surface =
  'record_keeping' | 'reconciliation' | 'ownership_verification' | 'chase_request' | 'other_ops'

export type PlacementMode = 'queue' | 'status' | 'admin' | 'document_state'

export interface Placement {
  /** null for document_state — it lives on no surface at all. */
  surface: Surface | null
  /** The officer QUEUE cell. null for every other mode. */
  subState: string | null
  mode?: PlacementMode
  statusLine?: string
  adminKind?: string
  /** The one verb the officer keeps on a system failure, if any. */
  officerVerb?: string
  documentStateLine?: string
}

export const SURFACE_ORDER: readonly Surface[] = [
  'record_keeping',
  'reconciliation',
  'ownership_verification',
  'chase_request',
  'other_ops',
]

export const SURFACE_NAMES: Record<Surface, string> = {
  record_keeping: 'RECORD KEEPING',
  reconciliation: 'RECONCILIATION',
  ownership_verification: 'OWNERSHIP VERIFICATION',
  chase_request: 'CHASE & REQUEST',
  other_ops: 'OTHER OPS',
}

/** URL slug ↔ surface key. The deep link is /workbench/<slug>?task=<id>. */
export const SURFACE_SLUGS: Record<Surface, string> = {
  record_keeping: 'record-keeping',
  reconciliation: 'reconciliation',
  ownership_verification: 'ownership-verification',
  chase_request: 'chase-request',
  other_ops: 'other-ops',
}

export function surfaceForSlug(slug: string | undefined): Surface | null {
  const hit = SURFACE_ORDER.find((s) => SURFACE_SLUGS[s] === slug)
  return hit ?? null
}

/** Sub-state key → printed label, per surface, in print order. */
export const SUB_STATES: Record<Surface, readonly { key: string; label: string }[]> = {
  record_keeping: [
    { key: 'to_decide', label: 'to decide' },
    { key: 'to_book', label: 'to book' },
    { key: 'to_walk', label: 'to walk' },
  ],
  // The FIFTH surface (locked 2026-08-31): reconciliation owns the
  // "to reconcile" queue, sequenced after bookings.
  reconciliation: [{ key: 'to_reconcile', label: 'to reconcile' }],
  ownership_verification: [{ key: 'to_sign_off', label: 'to verify' }],
  chase_request: [{ key: 'open_requests', label: 'open requests' }],
  // Other Ops for the OFFICER is the DIGEST and nothing else (Ale
  // 2026-08-09). The machine failures print on the same line as a
  // non-actionable admin segment — see ADMIN_KINDS.
  other_ops: [{ key: 'digest', label: 'digest' }],
}

/** The system-failure kinds on the OTHER OPS line's admin segment.
 *  `officerVerb` is the ONE verb the officer keeps on that kind. */
export const ADMIN_KINDS: readonly {
  key: string
  label: string
  officerVerb: string | null
}[] = [
  { key: 'stuck', label: 'stuck', officerVerb: 'retry' },
  { key: 'write_failed', label: 'write failed', officerVerb: 'retry' },
  { key: 'unrouted', label: 'unrouted', officerVerb: null },
]

export const ADMIN_SURFACE: Surface = 'other_ops'

/** Where the admin segment links. This is the contract with #1066: its
 *  failure-card grammar IS the admin view. Verified against that branch —
 *  it mounts SystemFailuresAdminPage at /v2/ops/system-failures behind an
 *  admin ProtectedRoute; a mismatch here is a dead link, not a test failure. */
export const ADMIN_VIEW_ROUTE = '/v2/ops/system-failures'

/** The safety net — the OTHER OPS line renders even at zero for this. */
export const UNROUTABLE: Placement = {
  surface: 'other_ops',
  subState: null,
  mode: 'admin',
  adminKind: 'unrouted',
}

export const PLACEMENT: Record<string, Placement> = {
  // ── RECORD KEEPING ──────────────────────────────────────────────────
  proposal_review: { surface: 'record_keeping', subState: 'to_decide' },
  // ── RECONCILIATION — the fifth surface (locked 2026-08-31) ──────────
  position_trade_missing: { surface: 'reconciliation', subState: 'to_reconcile' },
  position_balance_stale: { surface: 'reconciliation', subState: 'to_reconcile' },
  position_balance_rollforward: { surface: 'reconciliation', subState: 'to_reconcile' },
  position_leg_identity_violation: { surface: 'reconciliation', subState: 'to_reconcile' },
  instrument_trade_missing: { surface: 'reconciliation', subState: 'to_reconcile' },
  instrument_balance_stale: { surface: 'reconciliation', subState: 'to_reconcile' },
  commitment_supply_mismatch: { surface: 'reconciliation', subState: 'to_reconcile' },
  trade_duplicate_question: { surface: 'reconciliation', subState: 'to_reconcile' },
  look_through_review: { surface: 'record_keeping', subState: 'to_walk' },
  // ── OWNERSHIP VERIFICATION ──────────────────────────────────────────
  evidence_approval: { surface: 'ownership_verification', subState: 'to_sign_off' },
  verification_signoff: { surface: 'ownership_verification', subState: 'to_sign_off' },
  verification_remediation: { surface: 'ownership_verification', subState: 'to_sign_off' },
  // ── CHASE & REQUEST (OV-sourced only; RK never mints requests) ───────
  // investigation_action is a CHASE, never an ops failure — it must not
  // also be counted under Other Ops.
  investigation_action: { surface: 'chase_request', subState: 'open_requests' },
  request_overdue: { surface: 'chase_request', subState: 'open_requests' },
  matured_unconfirmed: { surface: 'chase_request', subState: 'open_requests' },
  // ── OTHER OPS — the officer's half is the digest ─────────────────────
  daily_digest: { surface: 'other_ops', subState: 'digest' },
  // ── SYSTEM FAILURES — admin's work, visible to the officer ───────────
  // Whole-group RETRY stays with the officer on both of these.
  document_stuck: {
    surface: 'other_ops',
    subState: null,
    mode: 'admin',
    adminKind: 'stuck',
    officerVerb: 'retry',
  },
  source_question: { surface: 'record_keeping', subState: 'to_decide', mode: 'queue' },
  dead_letter_review: {
    surface: 'other_ops',
    subState: null,
    mode: 'admin',
    adminKind: 'write_failed',
    officerVerb: 'retry',
  },
  // ── DOCUMENT STATE — not work; acting on it mints a request ──────────
  request_unlocked_copy: {
    surface: null,
    subState: null,
    mode: 'document_state',
    documentStateLine:
      'could not be read · password-protected · ask the sender for an unlocked copy',
  },
  // ── STATUS, NOT ROWS ────────────────────────────────────────────────
  chain_orphan: {
    surface: 'record_keeping',
    subState: null,
    mode: 'status',
    statusLine: 'no chain head yet',
  },
  look_through_incomplete: {
    surface: 'record_keeping',
    subState: null,
    mode: 'status',
    statusLine: 'no target yet',
  },
}

/** Never throws: an unknown type lands on the visible safety net. */
export function placementFor(taskType: string): Placement {
  return PLACEMENT[taskType] ?? UNROUTABLE
}

export function modeOf(taskType: string): PlacementMode {
  return placementFor(taskType).mode ?? 'queue'
}

/** The surface a task's stream lives on, or null when it lives on none
 *  (document state). Used by the queue filter and by the old-route
 *  redirect — deep links redirect BY TASK TYPE. */
export function surfaceFor(taskType: string): Surface | null {
  return placementFor(taskType).surface
}

/** The one verb the officer keeps on a system failure, if any. */
export function officerVerbFor(taskType: string): string | null {
  return placementFor(taskType).officerVerb ?? null
}

/** Every OFFICER-QUEUE task type on a surface. Status and admin
 *  placements are excluded by construction — neither is officer work. */
export function taskTypesForSurface(surface: Surface): string[] {
  return Object.entries(PLACEMENT)
    .filter(([, p]) => (p.mode ?? 'queue') === 'queue' && p.surface === surface)
    .map(([t]) => t)
    .sort()
}

/** Officer wording for a task type — the surface's own vocabulary, not
 *  the engine's snake_case. */
const TASK_TYPE_LABELS: Record<string, string> = {
  proposal_review: 'chain review',
  look_through_review: 'look-through walk',
  position_trade_missing: 'position needs a movement',
  instrument_trade_missing: 'instrument needs a movement',
  position_balance_stale: 'position balance out of date',
  position_balance_rollforward: 'balance can be brought forward',
  instrument_balance_stale: 'instrument balance out of date',
  position_leg_identity_violation: 'position legs disagree',
  commitment_supply_mismatch: 'commitments exceed supply',
  trade_duplicate_question: 'possible duplicate trade',
  evidence_approval: 'evidence sign-off',
  verification_signoff: 'fund sign-off',
  verification_remediation: 'sign-off lost',
  investigation_action: 'evidence to obtain',
  request_overdue: 'request overdue',
  matured_unconfirmed: 'matured, no closure document',
  document_stuck: 'document stalled',
  source_question: 'source details need clarification',
  dead_letter_review: 'write failed',
  request_unlocked_copy: 'could not be read',
  daily_digest: 'daily digest',
  chain_orphan: 'no chain head yet',
  look_through_incomplete: 'no target yet',
}

export function taskTypeLabel(taskType: string): string {
  return TASK_TYPE_LABELS[taskType] ?? taskType.replace(/_/g, ' ')
}
